import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { api, extractErrorMessage } from '../../../lib/api';
import { useTenantSettings } from '../../../contexts/TenantSettingsContext';

/**
 * Onglet "Général" — US-ADM-09 + localisation US-ADM-01.
 *
 * Concentre les paramètres opérationnels : localisation (fuseau, devise,
 * langue, formats), numérotation, workflows, constantes, délais notifs.
 */
export function GeneralSettingsTab() {
  const { settings, refresh } = useTenantSettings();
  // Localisation
  const [timezone, setTimezone] = useState('Europe/Paris');
  const [currency, setCurrency] = useState('EUR');
  const [language, setLanguage] = useState<'fr' | 'en'>('fr');
  const [dateFormat, setDateFormat] = useState<'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'>(
    'DD/MM/YYYY',
  );
  const [timeFormat, setTimeFormat] = useState<'24h' | '12h'>('24h');
  // Numérotation
  const [equipPrefix, setEquipPrefix] = useState('EQ-');
  const [intervPrefix, setIntervPrefix] = useState('INT-');
  const [resPrefix, setResPrefix] = useState('RES-');
  const [resetStrategy, setResetStrategy] = useState<'continuous' | 'yearly'>('continuous');
  // Workflows
  const [requireInt, setRequireInt] = useState(true);
  const [requireRes, setRequireRes] = useState(true);
  const [multilevel, setMultilevel] = useState(false);
  // Constantes
  const [techRate, setTechRate] = useState(50);
  // Délais notifs
  const [maintDays, setMaintDays] = useState('30, 7, 1');
  const [calibDays, setCalibDays] = useState('60, 30, 7');
  const [warrDays, setWarrDays] = useState('60, 30');

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    const s = settings;
    setTimezone(s.localization.timezone);
    setCurrency(s.localization.currency);
    setLanguage(s.localization.language);
    setDateFormat(s.localization.date_format);
    setTimeFormat(s.localization.time_format);
    setEquipPrefix(s.numbering.equipment_prefix);
    setIntervPrefix(s.numbering.intervention_prefix);
    setResPrefix(s.numbering.reservation_prefix);
    setResetStrategy(s.numbering.reset_strategy);
    setRequireInt(s.workflows.require_intervention_validation);
    setRequireRes(s.workflows.require_reservation_validation);
    setMultilevel(s.workflows.enable_multilevel_approval);
    setTechRate(s.constants.technician_hourly_rate);
    setMaintDays(s.notification_delays.maintenance_alert_days.join(', '));
    setCalibDays(s.notification_delays.calibration_alert_days.join(', '));
    setWarrDays(s.notification_delays.warranty_alert_days.join(', '));
  }, [settings]);

  if (!settings) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  function parseDays(input: string): number[] {
    return input
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n >= 0);
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        api.patch('/admin/tenant/localization', {
          timezone,
          currency,
          language,
          date_format: dateFormat,
          time_format: timeFormat,
        }),
        api.patch('/admin/tenant/numbering', {
          equipment_prefix: equipPrefix,
          intervention_prefix: intervPrefix,
          reservation_prefix: resPrefix,
          reset_strategy: resetStrategy,
        }),
        api.patch('/admin/tenant/workflows', {
          require_intervention_validation: requireInt,
          require_reservation_validation: requireRes,
          enable_multilevel_approval: multilevel,
        }),
        api.patch('/admin/tenant/constants', {
          technician_hourly_rate: techRate,
        }),
        api.patch('/admin/tenant/notifications', {
          maintenance_alert_days: parseDays(maintDays),
          calibration_alert_days: parseDays(calibDays),
          warranty_alert_days: parseDays(warrDays),
        }),
      ]);
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={onSave} className="space-y-6 max-w-3xl">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Localisation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Fuseau horaire">
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Devise (ISO 4217)">
            <input
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className={inputClass + ' uppercase'}
            />
          </Field>
          <Field label="Langue">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'fr' | 'en')}
              className={inputClass}
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </Field>
          <Field label="Format de date">
            <select
              value={dateFormat}
              onChange={(e) =>
                setDateFormat(e.target.value as 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD')
              }
              className={inputClass}
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </Field>
          <Field label="Format heure">
            <select
              value={timeFormat}
              onChange={(e) => setTimeFormat(e.target.value as '24h' | '12h')}
              className={inputClass}
            >
              <option value="24h">24h</option>
              <option value="12h">12h (AM/PM)</option>
            </select>
          </Field>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Numérotation automatique</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Préfixe équipements" hint="ex. EQ-, SITE-A-EQ-">
            <input
              value={equipPrefix}
              onChange={(e) => setEquipPrefix(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Préfixe interventions">
            <input
              value={intervPrefix}
              onChange={(e) => setIntervPrefix(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Préfixe réservations">
            <input
              value={resPrefix}
              onChange={(e) => setResPrefix(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Stratégie de reset">
            <select
              value={resetStrategy}
              onChange={(e) => setResetStrategy(e.target.value as 'continuous' | 'yearly')}
              className={inputClass}
            >
              <option value="continuous">Continu (jamais reset)</option>
              <option value="yearly">Reset chaque année</option>
            </select>
          </Field>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Workflows</h2>
        <div className="space-y-2">
          <Toggle
            label="Valider les interventions avant clôture"
            checked={requireInt}
            onChange={setRequireInt}
          />
          <Toggle
            label="Valider les réservations de salles"
            checked={requireRes}
            onChange={setRequireRes}
          />
          <Toggle
            label="Approbation multi-niveaux (futur)"
            checked={multilevel}
            onChange={setMultilevel}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Constantes métier</h2>
        <Field label={`Taux horaire technicien (${currency})`}>
          <input
            type="number"
            min={0}
            value={techRate}
            onChange={(e) => setTechRate(Number(e.target.value))}
            className={inputClass}
          />
        </Field>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Délais d'alerte (jours)</h2>
        <div className="space-y-2">
          <Field
            label="Maintenance préventive"
            hint="Listes de jours j-N, séparés par virgules. ex. 30, 7, 1"
          >
            <input
              value={maintDays}
              onChange={(e) => setMaintDays(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Étalonnage" hint="ex. 60, 30, 7">
            <input
              value={calibDays}
              onChange={(e) => setCalibDays(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Fin de garantie" hint="ex. 60, 30">
            <input
              value={warrDays}
              onChange={(e) => setWarrDays(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      </section>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        {savedAt && (
          <span className="inline-flex items-center text-sm text-green-600">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Enregistré
          </span>
        )}
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Enregistrer
        </button>
      </div>
    </form>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
}) {
  return (
    <label className="flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="ml-2 text-sm text-gray-700">{label}</span>
    </label>
  );
}
